// Les 10 besoins psychologiques — contenu bilingue FR / ES (document M2HAT Psychologie).
// Chaque besoin est évalué par 5 items sur une échelle 0–4 (score /20).

export const SATISFACTION_LABELS = {
  fr: [
    { value: 0, label: "Jamais" },
    { value: 1, label: "Rarement" },
    { value: 2, label: "Parfois" },
    { value: 3, label: "Souvent" },
    { value: 4, label: "Presque toujours" },
  ],
  es: [
    { value: 0, label: "Nunca" },
    { value: 1, label: "Raramente" },
    { value: 2, label: "A veces" },
    { value: 3, label: "A menudo" },
    { value: 4, label: "Casi siempre" },
  ],
};

export const INTERPRETATION = [
  { min: 16, max: 20, color: "#8A6D56", fr: "Besoin largement satisfait. Ressource psychologique stable.", es: "Necesidad ampliamente satisfecha. Recurso psicológico estable." },
  { min: 11, max: 15, color: "#D4A77B", fr: "Besoin globalement satisfait mais fragile. À surveiller.", es: "Necesidad globalmente satisfecha pero frágil. A vigilar." },
  { min: 6, max: 10, color: "#E99F4F", fr: "Besoin insuffisamment satisfait. Probable source de vulnérabilité.", es: "Necesidad insuficientemente satisfecha. Probable fuente de vulnerabilidad." },
  { min: 0, max: 5, color: "#FFC355", fr: "Besoin fortement frustré. Facteur clinique majeur.", es: "Necesidad fuertemente frustrada. Factor clínico importante." },
];

export const NEEDS = [
  {
    key: "securite",
    name: { fr: "Besoin de sécurité", es: "Necesidad de seguridad" },
    short: { fr: "Sécurité", es: "Seguridad" },
    definition: {
      fr: "Le besoin de sécurité correspond au sentiment de pouvoir vivre dans un environnement suffisamment stable, prévisible et protecteur pour se sentir serein.",
      es: "La necesidad de seguridad corresponde al sentimiento de poder vivir en un entorno suficientemente estable, previsible y protector para sentirse sereno.",
    },
    notList: {
      fr: ["vivre sans aucun risque ;", "contrôler toutes les situations ;", "éviter toute difficulté ou toute émotion désagréable."],
      es: ["vivir sin ningún riesgo;", "controlar todas las situaciones;", "evitar toda dificultad o toda emoción desagradable."],
    },
    items: {
      fr: [
        "Je peux anticiper ce qui va se passer dans mon quotidien.",
        "Je me sens protégé par mon entourage.",
        "Je n'ai pas peur qu'un problème survienne.",
        "Je peux me détendre sans être en état d'alerte.",
        "Je fais confiance à l'avenir.",
      ],
      es: [
        "Puedo anticipar lo que va a suceder en mi vida cotidiana.",
        "Me siento protegido por mi entorno.",
        "No tengo miedo de que surja un problema.",
        "Puedo relajarme sin estar en estado de alerta.",
        "Confío en el futuro.",
      ],
    },
  },
  {
    key: "attachement",
    name: { fr: "Besoin d'attachement", es: "Necesidad de apego" },
    short: { fr: "Attachement", es: "Apego" },
    definition: {
      fr: "Le besoin d'attachement est le besoin de créer des liens affectifs sécurisants, de se sentir aimé, accepté et soutenu par des personnes importantes.",
      es: "La necesidad de apego es la necesidad de crear vínculos afectivos seguros, de sentirse amado, aceptado y apoyado por personas importantes.",
    },
    notList: {
      fr: ["dépendre des autres ;", "être constamment entouré ;", "rechercher l'approbation de tout le monde."],
      es: ["depender de los demás;", "estar constantemente rodeado;", "buscar la aprobación de todo el mundo."],
    },
    items: {
      fr: [
        "Je me sens aimé.",
        "Je peux compter sur certaines personnes.",
        "Je me sens accepté tel que je suis.",
        "Je peux demander de l'aide sans culpabiliser.",
        "Je me sens émotionnellement proche d'au moins une personne.",
      ],
      es: [
        "Me siento amado.",
        "Puedo contar con ciertas personas.",
        "Me siento aceptado tal como soy.",
        "Puedo pedir ayuda sin sentirme culpable.",
        "Me siento emocionalmente cercano a al menos una persona.",
      ],
    },
  },
  {
    key: "autonomie",
    name: { fr: "Besoin d'autonomie", es: "Necesidad de autonomía" },
    short: { fr: "Autonomie", es: "Autonomía" },
    definition: {
      fr: "Le besoin d'autonomie correspond à la capacité d'agir librement, de faire des choix en accord avec ses valeurs et d'être acteur de sa vie.",
      es: "La necesidad de autonomía corresponde a la capacidad de actuar libremente, de tomar decisiones acordes con los propios valores y de ser protagonista de la propia vida.",
    },
    notList: {
      fr: ["tout faire seul ;", "refuser l'aide des autres ;", "s'opposer systématiquement."],
      es: ["hacerlo todo solo;", "rechazar la ayuda de los demás;", "oponerse sistemáticamente."],
    },
    items: {
      fr: [
        "J'ai le sentiment de prendre des décisions qui me ressemblent.",
        "Je peux dire non lorsque c'est nécessaire.",
        "Je me sens libre d'organiser mon quotidien.",
        "Je dirige ma vie plutôt que de la subir.",
        "Je peux exprimer mes besoins librement.",
      ],
      es: [
        "Tengo la sensación de tomar decisiones que se parecen a mí.",
        "Puedo decir no cuando es necesario.",
        "Me siento libre de organizar mi vida cotidiana.",
        "Dirijo mi vida en lugar de padecerla.",
        "Puedo expresar mis necesidades libremente.",
      ],
    },
  },
  {
    key: "competence",
    name: { fr: "Besoin de compétence", es: "Necesidad de competencia" },
    short: { fr: "Compétence", es: "Competencia" },
    definition: {
      fr: "Le besoin de compétence est le sentiment d'être capable de relever les défis, d'apprendre et d'agir efficacement dans son quotidien.",
      es: "La necesidad de competencia es el sentimiento de ser capaz de afrontar los retos, de aprender y de actuar eficazmente en la vida cotidiana.",
    },
    notList: {
      fr: ["être parfait(e) ;", "réussir en permanence ;", "tout savoir."],
      es: ["ser perfecto(a);", "tener éxito permanentemente;", "saberlo todo."],
    },
    items: {
      fr: [
        "Je me sens capable de faire face aux difficultés.",
        "J'ai confiance dans mes capacités.",
        "Je progresse dans ce qui est important pour moi.",
        "J'apprends de mes erreurs.",
        "Je me sens efficace dans mon quotidien.",
      ],
      es: [
        "Me siento capaz de afrontar las dificultades.",
        "Confío en mis capacidades.",
        "Progreso en lo que es importante para mí.",
        "Aprendo de mis errores.",
        "Me siento eficaz en mi vida cotidiana.",
      ],
    },
  },
  {
    key: "reconnaissance",
    name: { fr: "Besoin de reconnaissance", es: "Necesidad de reconocimiento" },
    short: { fr: "Reconnaissance", es: "Reconocimiento" },
    definition: {
      fr: "Le besoin de reconnaissance correspond au besoin de voir ses efforts, ses qualités et sa valeur personnelle reconnus par les autres.",
      es: "La necesidad de reconocimiento corresponde a la necesidad de ver reconocidos los propios esfuerzos, cualidades y valor personal por los demás.",
    },
    notList: {
      fr: ["rechercher les compliments en permanence ;", "dépendre du regard des autres ;", "avoir besoin d'être admiré."],
      es: ["buscar los elogios permanentemente;", "depender de la mirada de los demás;", "necesitar ser admirado."],
    },
    items: {
      fr: [
        "Mes efforts sont reconnus.",
        "Je me sens apprécié.",
        "Les autres remarquent mes qualités.",
        "Je me sens valorisé.",
        "Je reçois suffisamment de retours positifs.",
      ],
      es: [
        "Mis esfuerzos son reconocidos.",
        "Me siento apreciado.",
        "Los demás notan mis cualidades.",
        "Me siento valorado.",
        "Recibo suficientes comentarios positivos.",
      ],
    },
  },
  {
    key: "identite",
    name: { fr: "Besoin d'identité", es: "Necesidad de identidad" },
    short: { fr: "Identité", es: "Identidad" },
    definition: {
      fr: "Le besoin d'identité est le besoin de savoir qui l'on est, de connaître ses valeurs, ses limites et ce qui est important pour soi.",
      es: "La necesidad de identidad es la necesidad de saber quién se es, de conocer los propios valores, límites y lo que es importante para uno mismo.",
    },
    notList: {
      fr: ["avoir une personnalité figée ;", "ne jamais changer ;", "devoir toujours être sûr de soi."],
      es: ["tener una personalidad rígida;", "no cambiar nunca;", "tener que estar siempre seguro de uno mismo."],
    },
    items: {
      fr: [
        "Je sais qui je suis.",
        "Mes valeurs sont claires.",
        "Je reste fidèle à moi-même.",
        "Je n'ai pas besoin de jouer un rôle.",
        "Je sais ce qui est important pour moi.",
      ],
      es: [
        "Sé quién soy.",
        "Mis valores son claros.",
        "Me mantengo fiel a mí mismo.",
        "No necesito interpretar un papel.",
        "Sé lo que es importante para mí.",
      ],
    },
  },
  {
    key: "controle",
    name: { fr: "Besoin de contrôle", es: "Necesidad de control" },
    short: { fr: "Contrôle", es: "Control" },
    definition: {
      fr: "Le besoin de contrôle correspond au sentiment de pouvoir agir sur ce qui dépend de soi et de faire face aux événements de manière adaptée.",
      es: "La necesidad de control corresponde al sentimiento de poder actuar sobre lo que depende de uno mismo y de afrontar los acontecimientos de manera adaptada.",
    },
    notList: {
      fr: ["tout maîtriser ;", "empêcher les imprévus ;", "contrôler les autres."],
      es: ["controlarlo todo;", "impedir los imprevistos;", "controlar a los demás."],
    },
    items: {
      fr: [
        "J'ai le sentiment d'influencer ce qui dépend de moi.",
        "Je peux agir lorsque quelque chose ne me convient pas.",
        "Je gère efficacement les imprévus.",
        "Je sais réguler mes émotions.",
        "Je distingue ce que je peux contrôler de ce que je ne peux pas contrôler.",
      ],
      es: [
        "Tengo la sensación de influir en lo que depende de mí.",
        "Puedo actuar cuando algo no me conviene.",
        "Gestiono eficazmente los imprevistos.",
        "Sé regular mis emociones.",
        "Distingo lo que puedo controlar de lo que no puedo controlar.",
      ],
    },
  },
  {
    key: "plaisir",
    name: { fr: "Besoin de plaisir", es: "Necesidad de placer" },
    short: { fr: "Plaisir", es: "Placer" },
    definition: {
      fr: "Le besoin de plaisir est la capacité à ressentir des émotions agréables, à profiter des bons moments et à s'accorder des activités ressourçantes.",
      es: "La necesidad de placer es la capacidad de sentir emociones agradables, de disfrutar de los buenos momentos y de concederse actividades reconfortantes.",
    },
    notList: {
      fr: ["rechercher le plaisir en permanence ;", "éviter toute contrainte ;", "vivre uniquement pour le divertissement."],
      es: ["buscar el placer permanentemente;", "evitar toda obligación;", "vivir únicamente para el entretenimiento."],
    },
    items: {
      fr: [
        "Je prends du plaisir régulièrement.",
        "Je profite des moments agréables.",
        "Je ris facilement.",
        "Je m'autorise à me détendre.",
        "Je fais des activités qui me procurent de la joie.",
      ],
      es: [
        "Disfruto con regularidad.",
        "Aprovecho los momentos agradables.",
        "Me río con facilidad.",
        "Me permito relajarme.",
        "Hago actividades que me producen alegría.",
      ],
    },
  },
  {
    key: "sens",
    name: { fr: "Besoin de sens", es: "Necesidad de sentido" },
    short: { fr: "Sens", es: "Sentido" },
    definition: {
      fr: "Le besoin de sens est le besoin de donner une direction à sa vie, de comprendre pourquoi l'on agit et de se sentir en accord avec ses valeurs.",
      es: "La necesidad de sentido es la necesidad de dar una dirección a la propia vida, de comprender por qué se actúa y de sentirse en coherencia con los propios valores.",
    },
    notList: {
      fr: ["avoir réponse à toutes les questions ;", "poursuivre un objectif extraordinaire ;", "réussir socialement."],
      es: ["tener respuesta a todas las preguntas;", "perseguir un objetivo extraordinario;", "tener éxito socialmente."],
    },
    items: {
      fr: [
        "Ma vie a du sens.",
        "Aujourd'hui, je comprends pourquoi je fais ce que je fais.",
        "Mes actions correspondent à mes valeurs.",
        "Je me sens utile.",
        "Je poursuis des objectifs qui ont du sens pour moi.",
      ],
      es: [
        "Mi vida tiene sentido.",
        "Hoy comprendo por qué hago lo que hago.",
        "Mis acciones corresponden a mis valores.",
        "Me siento útil.",
        "Persigo objetivos que tienen sentido para mí.",
      ],
    },
  },
  {
    key: "accomplissement",
    name: { fr: "Besoin d'accomplissement", es: "Necesidad de realización" },
    short: { fr: "Accomplissement", es: "Realización" },
    definition: {
      fr: "Le besoin d'accomplissement correspond au besoin de progresser, d'utiliser son potentiel et d'éprouver de la satisfaction face à ce que l'on construit.",
      es: "La necesidad de realización corresponde a la necesidad de progresar, de utilizar el propio potencial y de sentir satisfacción por lo que se construye.",
    },
    notList: {
      fr: ["réussir plus que les autres ;", "être performant(e) en permanence ;", "accumuler les succès ou les récompenses."],
      es: ["tener más éxito que los demás;", "rendir permanentemente;", "acumular éxitos o recompensas."],
    },
    items: {
      fr: [
        "Je suis fier(e) de mes réalisations.",
        "Je termine ce que j'entreprends.",
        "Je développe mon potentiel.",
        "Je progresse vers mes objectifs.",
        "Je suis satisfait(e) de mes efforts.",
      ],
      es: [
        "Estoy orgulloso(a) de mis logros.",
        "Termino lo que emprendo.",
        "Desarrollo mi potencial.",
        "Progreso hacia mis objetivos.",
        "Estoy satisfecho(a) de mis esfuerzos.",
      ],
    },
  },
];

// Image d'ambiance apaisante par besoin.
export const NEED_IMAGES = {
  securite: "https://images.unsplash.com/photo-1769690399035-2f4e60edf2ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  attachement: "https://images.unsplash.com/photo-1506014299253-3725319c0f69?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  autonomie: "https://images.unsplash.com/photo-1495395226200-8fbf6b720b8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  competence: "https://images.unsplash.com/photo-1590605095243-072811dbe64c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  reconnaissance: "https://images.unsplash.com/photo-1651372381086-9861c9c81db5?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  identite: "https://images.unsplash.com/photo-1781819114972-478f14095968?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  controle: "https://images.unsplash.com/photo-1601779144625-d149a6214af7?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  plaisir: "https://images.unsplash.com/photo-1765084379870-1e005cdd3952?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  sens: "https://images.unsplash.com/photo-1464788061904-b026adb5422b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  accomplissement: "https://images.unsplash.com/photo-1464746133101-a2c3f88e0dd9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
};

// Textes d'interface du module (bilingues).
export const BESOINS_T = {
  fr: {
    allModules: "← Tous les modules",
    welcomeTitle: "Évaluation des besoins psychologiques",
    welcomeText:
      "Ce questionnaire vous invite à explorer, en toute confidentialité, la manière dont vos besoins psychologiques fondamentaux sont satisfaits aujourd'hui. Prenez le temps nécessaire : il n'existe pas de bonne ou de mauvaise réponse.",
    start: "Commencer",
    presTitle: "Présentation du questionnaire",
    pres1: "Ce questionnaire évalue 10 besoins psychologiques fondamentaux : sécurité, attachement, autonomie, compétence, reconnaissance, identité, contrôle, plaisir, sens et accomplissement.",
    pres2: "Pour chaque besoin, vous répondrez à 5 affirmations. Répondez spontanément, en fonction de votre ressenti actuel.",
    pres3: "Aucun résultat ne s'affichera pendant la passation. Vous découvrirez l'ensemble de vos résultats uniquement à la fin, une fois toutes les étapes complétées.",
    cotTitle: "Explication de la cotation",
    cotText: "Pour chaque affirmation, indiquez à quelle fréquence elle correspond à votre vécu :",
    cotNote: "Répondez spontanément à chaque affirmation. Vos scores et leur interprétation vous seront présentés uniquement à la fin.",
    prev: "Précédent",
    next: "Suivant",
    beginPassation: "Commencer la passation",
    needLabel: "Besoin",
    needOf: "sur",
    completeItems: "Merci de répondre à tous les items pour continuer.",
    estTitle: "Selon vous, à quel point chacun de ces besoins est-il satisfait aujourd'hui ?",
    est1: "Vous venez de répondre aux questions portant sur la satisfaction actuelle de vos besoins psychologiques.",
    est2: "Avant de découvrir vos résultats, nous vous proposons d'estimer vous-même, de façon globale, le niveau de satisfaction que vous ressentez pour chacun de ces besoins, sur une échelle de 0 à 20.",
    est3: "Il n'existe pas de bonne ou de mauvaise réponse. Cette étape permettra de comparer votre perception globale à ce que révèle le détail de vos réponses.",
    notLabel: "Ce besoin n'est pas",
    lowSat: "Peu satisfait (0)",
    highSat: "Pleinement satisfait (20)",
    seeResults: "Voir mes résultats",
    completeSliders: "Merci de positionner le curseur pour chaque besoin afin de continuer.",
    // Results
    thanks: "Merci d'avoir complété ce questionnaire",
    thanksText: "Les résultats qui suivent constituent un support de réflexion clinique. Ils ne représentent pas un diagnostic mais une photographie de vos besoins psychologiques au moment de cette évaluation.",
    radar1: "Satisfaction des besoins",
    radar1Sub: "Mesurée à partir de vos réponses — 0 à 20",
    radar2: "Satisfaction estimée",
    radar2Sub: "Votre auto-évaluation globale — 0 à 20",
    interpTitle: "Interprétation des scores",
    interpSub: "Grille de lecture des scores de satisfaction (/20)",
    gapTitle: "Écart entre votre perception et vos réponses",
    gapSub: "Satisfaction estimée − satisfaction mesurée (en points)",
    gapText: "Un écart proche de zéro traduit une perception juste. Un écart positif signale une surestimation (vous vous sentez plus satisfait que ce que révèlent vos réponses), un écart négatif une sous-estimation.",
    recapTitle: "Tableau récapitulatif",
    colNeed: "Besoin",
    colMeasured: "Satisfaction mesurée (/20)",
    colEstimated: "Satisfaction estimée (/20)",
    colGap: "Écart",
    rankResources: "Vos principales ressources",
    rankResourcesNote: "Les 3 besoins les plus satisfaits (mesuré)",
    rankFragile: "Vos besoins les plus fragilisés",
    rankFragileNote: "Les 3 scores mesurés les plus faibles",
    rankOver: "Besoins que vous surestimez",
    rankOverNote: "Perception plus favorable que vos réponses",
    rankUnder: "Besoins que vous sous-estimez",
    rankUnderNote: "Réponses plus favorables que votre perception",
    noneCat: "Aucun besoin dans cette catégorie.",
    ecartNote: "L'écart met en lumière la justesse de votre perception : les besoins fortement surestimés ou sous-estimés méritent d'être explorés, car votre ressenti global s'y éloigne du détail de vos réponses.",
    synthTitle: "Synthèse clinique",
    exportPdf: "Exporter en PDF",
    restart: "Nouvelle passation",
    thresholds: { t5: "5 — frustration", t10: "10 — insuffisant", t15: "15 — fragile" },
  },
  es: {
    allModules: "← Todos los módulos",
    welcomeTitle: "Evaluación de las necesidades psicológicas",
    welcomeText:
      "Este cuestionario te invita a explorar, con total confidencialidad, la manera en que tus necesidades psicológicas fundamentales están satisfechas hoy. Tómate el tiempo necesario: no hay respuestas correctas ni incorrectas.",
    start: "Empezar",
    presTitle: "Presentación del cuestionario",
    pres1: "Este cuestionario evalúa 10 necesidades psicológicas fundamentales: seguridad, apego, autonomía, competencia, reconocimiento, identidad, control, placer, sentido y realización.",
    pres2: "Para cada necesidad responderás a 5 afirmaciones. Responde con espontaneidad, según lo que sientes actualmente.",
    pres3: "No se mostrará ningún resultado durante la pasación. Descubrirás todos tus resultados únicamente al final, una vez completadas todas las etapas.",
    cotTitle: "Explicación de la puntuación",
    cotText: "Para cada afirmación, indica con qué frecuencia corresponde a tu vivencia:",
    cotNote: "Responde con espontaneidad a cada afirmación. Tus puntuaciones y su interpretación se mostrarán únicamente al final.",
    prev: "Anterior",
    next: "Siguiente",
    beginPassation: "Comenzar el cuestionario",
    needLabel: "Necesidad",
    needOf: "de",
    completeItems: "Responde a todos los ítems para continuar.",
    estTitle: "Según tú, ¿en qué medida está satisfecha hoy cada una de estas necesidades?",
    est1: "Acabas de responder a las preguntas sobre la satisfacción actual de tus necesidades psicológicas.",
    est2: "Antes de descubrir tus resultados, te proponemos estimar tú mismo, de forma global, el nivel de satisfacción que sientes para cada una de estas necesidades, en una escala de 0 a 20.",
    est3: "No hay respuestas correctas ni incorrectas. Esta etapa permitirá comparar tu percepción global con lo que revela el detalle de tus respuestas.",
    notLabel: "Esta necesidad no es",
    lowSat: "Poco satisfecha (0)",
    highSat: "Plenamente satisfecha (20)",
    seeResults: "Ver mis resultados",
    completeSliders: "Coloca el cursor para cada necesidad para continuar.",
    thanks: "Gracias por completar este cuestionario",
    thanksText: "Los resultados siguientes constituyen un apoyo de reflexión clínica. No representan un diagnóstico, sino una fotografía de tus necesidades psicológicas en el momento de esta evaluación.",
    radar1: "Satisfacción de las necesidades",
    radar1Sub: "Medida a partir de tus respuestas — 0 a 20",
    radar2: "Satisfacción estimada",
    radar2Sub: "Tu autoevaluación global — 0 a 20",
    interpTitle: "Interpretación de las puntuaciones",
    interpSub: "Guía de lectura de las puntuaciones de satisfacción (/20)",
    gapTitle: "Diferencia entre tu percepción y tus respuestas",
    gapSub: "Satisfacción estimada − satisfacción medida (en puntos)",
    gapText: "Una diferencia cercana a cero refleja una percepción ajustada. Una diferencia positiva indica una sobrestimación (te sientes más satisfecho de lo que revelan tus respuestas), una diferencia negativa una subestimación.",
    recapTitle: "Tabla resumen",
    colNeed: "Necesidad",
    colMeasured: "Satisfacción medida (/20)",
    colEstimated: "Satisfacción estimada (/20)",
    colGap: "Diferencia",
    rankResources: "Tus principales recursos",
    rankResourcesNote: "Las 3 necesidades más satisfechas (medido)",
    rankFragile: "Tus necesidades más frágiles",
    rankFragileNote: "Las 3 puntuaciones medidas más bajas",
    rankOver: "Necesidades que sobrestimas",
    rankOverNote: "Percepción más favorable que tus respuestas",
    rankUnder: "Necesidades que subestimas",
    rankUnderNote: "Respuestas más favorables que tu percepción",
    noneCat: "Ninguna necesidad en esta categoría.",
    ecartNote: "La diferencia pone de relieve el ajuste de tu percepción: las necesidades muy sobrestimadas o subestimadas merecen explorarse, ya que tu sensación global se aleja del detalle de tus respuestas.",
    synthTitle: "Síntesis clínica",
    exportPdf: "Exportar en PDF",
    restart: "Nueva pasación",
    thresholds: { t5: "5 — frustración", t10: "10 — insuficiente", t15: "15 — frágil" },
  },
};
